import {
  EAnalyticsEvent,
  EAnalyticsKnowledgeScope,
} from "@frontend-shared/product-analytics";
import { EFileUploadStatus } from "@frontend-shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { knowledgeApi } from "@/api/services";
import { useProductAnalytics } from "@/hooks/use-product-analytics";
import { useTrackedFileUpload } from "@/hooks/use-tracked-file-upload";

import { FOLDER_KNOWLEDGE_FILES_QUERY_KEY } from "./useGetFolderKnowledgeFiles";
import { useKnowledgeUploadGuards } from "./useKnowledgeUploadGuards";

// NOTE: Folder-scoped counterpart of useUploadKnowledgeFile — reuses the shared
// tracked-upload state machine and invalidates only this folder's file list.
export const useUploadFolderKnowledgeFile = (folderUuid: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("knowledge");
  const { capture } = useProductAnalytics();
  const guards = useKnowledgeUploadGuards();

  return useTrackedFileUpload<File>({
    upload: (file, { signal, onProgress }) =>
      knowledgeApi.uploadFolderFile({ folderUuid, file, signal, onProgress }),
    buildEntry: (file) => ({
      id: crypto.randomUUID(),
      fileName: file.name,
      progress: 0,
      status: EFileUploadStatus.UPLOADING,
    }),
    onSuccess: () => {
      // NOTE: NSAI-4616 — same event as the global upload, split by scope, so
      // one number answers "is knowledge used" and a breakdown answers
      // "chat-scoped or library-wide".
      capture({
        event: EAnalyticsEvent.KNOWLEDGE_FILE_UPLOADED,
        properties: { scope: EAnalyticsKnowledgeScope.FOLDER },
      });
      queryClient.invalidateQueries({
        queryKey: [FOLDER_KNOWLEDGE_FILES_QUERY_KEY, folderUuid],
      });
    },
    errorFallback: () => t("error.uploadFailed"),
    // NOTE: NSAI-7817 — upload cap, size pre-check, readable reasons.
    ...guards,
  });
};
